import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import block from 'bem-cn'

import { PaymentMethods, PaymentTypes } from 'services/api/PaymentApi/types/common'

import { nowPaymentsSlug } from 'shared/constants/payment'

import { selectIsUserAuthenticated, selectUserData } from 'features/auth/selectors'
import { selectLocaleDict } from 'features/locale/selectors'
import { getCryptoCurrenciesList, getDepositSystemList } from 'features/payment/actions'
import {
  selectCryptoCurrenciesList,
  selectDepositSystemList,
  selectGetCryptoCurrencyListCommunication,
  selectGetDepositSystemListCommunication,
} from 'features/payment/selectors'

import { NowPayments } from 'pages/ProfilePage/components/NowPayments/NowPayments'

import { additionalDepositSystems, allowedCryptoCurrencies } from '../../data/utils'
import { AdditionalPayment } from '../AdditionalPayment'
import { ChoosePayment } from '../ChoosePayment/ChoosePayment'
import { CurrentPayment } from '../CurrentPayment/CurrentPayment'

import './Deposit.scss'

const b = block('deposit')

export const Deposit = () => {
  const dispatch = useDispatch()

  const isAuth = useSelector(selectIsUserAuthenticated, shallowEqual)
  const locale = useSelector(selectLocaleDict, shallowEqual).payment
  const user = useSelector(selectUserData, shallowEqual)

  const { list: systemList } = useSelector(selectDepositSystemList, shallowEqual)
  const cryptoList = useSelector(selectCryptoCurrenciesList, shallowEqual)
  const isSystemComm = useSelector(selectGetDepositSystemListCommunication, shallowEqual)
  const isCryptoComm = useSelector(selectGetCryptoCurrencyListCommunication, shallowEqual)

  const [selectedSystem, changeSelectedSystem] = useState('')

  useEffect(() => {
    if (isAuth) {
      dispatch(getDepositSystemList())
      dispatch(getCryptoCurrenciesList())
    }
  }, [])

  const onChangePaymentSystem = (value: string): void => {
    changeSelectedSystem(value)
  }

  const handleClear = useCallback(() => {
    changeSelectedSystem('')
  }, [])

  const isLoading = useMemo(
    () => isSystemComm.isLoading || isCryptoComm.isLoading,
    [isCryptoComm.isLoading, isSystemComm.isLoading],
  )

  const filteredSystemList = useMemo(
    () =>
      [...systemList, ...additionalDepositSystems]?.filter(system =>
        system.method === PaymentMethods.CRYPTO ? !isCryptoComm.isLoading : true,
      ),
    [isCryptoComm.isLoading, systemList],
  )

  const filteredCryptoList = useMemo(
    () => cryptoList?.filter(item => allowedCryptoCurrencies.includes(item.currency)),
    [cryptoList],
  )

  const currentPaymentComponent = useMemo(() => {
    let component = null

    const findAdditionData = additionalDepositSystems.find(system => system.slug === selectedSystem)

    if (selectedSystem === nowPaymentsSlug) {
      component = <NowPayments changeSystem={onChangePaymentSystem} locale={locale} cryptoList={filteredCryptoList} />
    } else if (findAdditionData?.slug) {
      component = (
        <AdditionalPayment
          paymentType={PaymentTypes.DEPOSIT}
          systemItem={findAdditionData}
          locale={locale}
          changeSystem={onChangePaymentSystem}
        />
      )
    } else if (selectedSystem) {
      component = (
        <CurrentPayment
          systemList={systemList}
          user={user}
          selectedSystem={selectedSystem}
          paymentType={PaymentTypes.DEPOSIT}
          changeSystem={onChangePaymentSystem}
        />
      )
    }
    return component
  }, [filteredCryptoList, handleClear, locale, selectedSystem, systemList, user])

  return (
    <div className={b('container')}>
      {!selectedSystem ? (
        <div className={b('block-wrapper')}>
          <ChoosePayment
            changeSystem={onChangePaymentSystem}
            systemList={filteredSystemList}
            cryptoList={[]}
            locale={locale}
            isCrypto={selectedSystem === nowPaymentsSlug}
            isLoading={isLoading}
          />
        </div>
      ) : (
        <div className={b('block-wrapper')}>{currentPaymentComponent}</div>
      )}
    </div>
  )
}

export default Deposit
